/**
 * Compile SLAM item content using the template field.
 * Template defines variables (e.g. count=min:1;max:10, obj=type:fruits) that are
 * computed at display time and substituted into description/response/introduction/indication.
 * Also expands <eval>expression</eval> and <object>name, count</object> tags.
 */

export interface TemplateVariable {
  variable: string;
  value: string | number;
  type?: string;
  render?: 'image' | 'emoji';
}

// Emoji mapping for objects (fruits, animals)
const OBJECT_EMOJIS: Record<string, Record<string, string>> = {
  fruits: {
    apple: '🍎',
    banana: '🍌',
    orange: '🍊',
    pear: '🍐',
    grape: '🍇',
    strawberry: '🍓',
    watermelon: '🍉',
  },
  animals: {
    cat: '🐱',
    dog: '🐕',
    rabbit: '🐰',
    elephant: '🐘',
    lion: '🦁',
    monkey: '🐒',
    bird: '🐦',
    fish: '🐠',
  },
};

const FLAT_EMOJIS: Record<string, string> = {
  ...OBJECT_EMOJIS.fruits,
  ...OBJECT_EMOJIS.animals,
};

function getRandomFromCategory(category: 'fruits' | 'animals'): string {
  const items = Object.keys(OBJECT_EMOJIS[category]);
  return items[Math.floor(Math.random() * items.length)];
}

function renderObjects(
  objectName: string,
  count: number,
  _renderMode: 'emoji' | 'image' = 'emoji'
): string {
  const emoji = FLAT_EMOJIS[objectName.toLowerCase()] || '❓';
  const n = Math.max(0, Math.min(count, 50));
  return Array(n).fill(emoji).join(' ');
}

function gcd(a: number, b: number): number {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) {
    const t = b;
    b = a % b;
    a = t;
  }
  return a;
}

function lcm(a: number, b: number): number {
  return (a * b) / gcd(a, b);
}

/**
 * Parse template string into variables with computed values.
 * Format: "var=min:1;max:10" or "var=type:fruits" or "obj=type:animals count=min:1;max:3" etc.
 */
export function parseTemplate(template: string): TemplateVariable[] {
  if (!template || !template.trim()) return [];

  const result: TemplateVariable[] = [];
  const parts = template.trim().split(/\s+/);

  for (const it of parts) {
    const eqIndex = it.indexOf('=');
    if (eqIndex === -1) continue;

    const variable = it.slice(0, eqIndex).trim();
    const rest = it.slice(eqIndex + 1);
    let min = -1e9;
    let max = 1e9;
    let type: string | undefined;
    let value: string | number = '';
    let render: 'image' | 'emoji' | undefined;

    const segments = rest.split(';');
    for (const p of segments) {
      if (p.includes('min:')) {
        min = parseInt(p.replace(/.*min:(\d+).*/, '$1'), 10) || min;
      }
      if (p.includes('max:')) {
        max = parseInt(p.replace(/.*max:(\d+).*/, '$1'), 10) || max;
      }
      if (p.includes('type:')) {
        const typeValue = p.substring(p.indexOf('type:') + 5).trim();
        if (typeValue === 'fruits' || typeValue === 'animals') {
          type = getRandomFromCategory(typeValue);
          value = type;
        } else if (typeValue === 'table') {
          type = 'table';
          const rows = parseInt(
            segments.find(s => s.startsWith('rows:'))?.split(':')[1] || '3',
            10
          );
          const cols = parseInt(
            segments.find(s => s.startsWith('cols:'))?.split(':')[1] || '3',
            10
          );
          const uppercase = !segments.some(s => s === 'case:lower');
          const wordPart = segments.find(s => s.startsWith('word:'));
          const wordLength = wordPart ? parseInt(wordPart.split(':')[1], 10) : undefined;
          const numPart = segments.find(s => s.startsWith('number:'));
          let numberMin: number | undefined;
          let numberMax: number | undefined;
          if (numPart) {
            const minMatch = rest.match(/number:min:(\d+)/);
            const maxMatch = rest.match(/max:(\d+)/);
            if (minMatch) numberMin = parseInt(minMatch[1], 10);
            if (maxMatch) numberMax = parseInt(maxMatch[1], 10);
          }
          value = generateTable(
            rows,
            cols,
            uppercase,
            wordLength,
            numberMin,
            numberMax
          );
        } else {
          type = typeValue;
          value = typeValue;
        }
      }
      if (p.includes('render:')) {
        const r = p.substring(p.indexOf('render:') + 7).trim();
        if (r === 'image' || r === 'emoji') render = r as 'image' | 'emoji';
      }
    }

    const hasMinMax =
      segments.some(s => s.includes('min:')) || segments.some(s => s.includes('max:'));
    if (hasMinMax && value === '' && !type) {
      value = Math.floor(Math.random() * (max - min + 1)) + min;
    }

    if (typeof value === 'number' || value !== '') {
      result.push({ variable, value, type, render });
    }
  }

  return result;
}

function generateTable(
  rows: number,
  cols: number,
  uppercase: boolean,
  wordLength?: number,
  numberMin?: number,
  numberMax?: number
): string {
  const consonants = 'BCDFGHJKLMNPQRSTVWXYZ';
  const vowels = 'AEIOU';
  const randLetter = () => {
    const c = consonants[Math.floor(Math.random() * consonants.length)];
    const v = vowels[Math.floor(Math.random() * vowels.length)];
    const s = c + v;
    return uppercase ? s : s.toLowerCase();
  };
  const randWord = (len: number) => {
    let w = '';
    for (let i = 0; i < len; i++) w += randLetter();
    return w;
  };

  let table = '<table class="letter-table" style="border-collapse:collapse;margin:8px 0;">';
  for (let i = 0; i < rows; i++) {
    table += '<tr>';
    for (let j = 0; j < cols; j++) {
      let cell: string;
      if (numberMin !== undefined && numberMax !== undefined) {
        cell = String(
          Math.floor(Math.random() * (numberMax - numberMin + 1)) + numberMin
        );
      } else if (wordLength) {
        cell = randWord(wordLength);
      } else {
        cell = randLetter();
      }
      table += `<td style="border:1px solid #ccc;padding:4px 8px;">${cell}</td>`;
    }
    table += '</tr>';
  }
  table += '</table>';
  return table;
}

function unescapeHtml(s: string): string {
  if (!s) return s;
  return s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/&nbsp;/g, ' ');
}

/**
 * Substitute variable names with their values in text.
 */
function substituteVariables(text: string, variables: TemplateVariable[]): string {
  if (!text) return text;
  let out = text;
  for (const v of variables) {
    const strVal = String(v.value);
    out = out.split(v.variable).join(strVal);
  }
  return out;
}

/**
 * Expand <eval>...</eval> and <object>...</object> in text.
 * Variables array is used to resolve object name/count from variable names.
 */
function processEvalAndObject(
  text: string,
  variables: TemplateVariable[]
): string {
  if (!text) return text;

  const evalPattern = /<eval>([^<]*)<\/eval>/gi;
  let out = text.replace(evalPattern, (_, expr) => {
    try {
      // eslint-disable-next-line no-new-func
      const fn = new Function('gcd', 'lcm', `return (${expr.trim()});`);
      const result = fn(gcd, lcm);
      return String(result);
    } catch (e) {
      console.warn('slamTemplateCompiler: eval failed', expr, e);
      return expr;
    }
  });

  const objectPattern = /<object>([^<]*)<\/object>/gi;
  out = out.replace(objectPattern, (_, expr) => {
    try {
      const [objectName, countStr] = expr.split(',').map((s: string) => s.trim());
      const objectVar = variables.find(v => v.variable === objectName && v.type);
      const actualObjectName = objectVar ? (objectVar.type as string) : objectName;
      const countVar = variables.find(v => v.variable === countStr);
      const count = countVar
        ? (typeof countVar.value === 'number' ? countVar.value : parseInt(String(countVar.value), 10))
        : parseInt(countStr, 10);
      const renderMode =
        objectVar && (objectVar as any).render === 'image' ? 'image' : 'emoji';
      return renderObjects(actualObjectName, isNaN(count) ? 1 : count, renderMode);
    } catch (e) {
      console.warn('slamTemplateCompiler: object failed', expr, e);
      return expr;
    }
  });

  const objectImgPattern = /<objectImg>([^<]*)<\/objectImg>/gi;
  out = out.replace(objectImgPattern, (_, expr) => {
    try {
      const [objectName, countStr] = expr.split(',').map((s: string) => s.trim());
      const count = parseInt(countStr, 10);
      return renderObjects(objectName, isNaN(count) ? 1 : count, 'image');
    } catch (e) {
      return expr;
    }
  });

  return out;
}

export interface CompileSlamContentInput {
  template?: string;
  description?: string;
  response?: string;
  introduction?: string;
  indication?: string;
  choices?: (string | number)[];
}

export interface CompileSlamContentOutput {
  description: string;
  response: string;
  introduction: string;
  indication: string;
  choices?: (string | number)[];
}

/**
 * Compile SLAM item content: parse template, substitute variables, expand <eval> and <object>.
 * LaTeX ($...$, <latex>...</latex>) is left as-is for LaTeXRenderer to handle later.
 */
export function compileSlamContent(input: CompileSlamContentInput): CompileSlamContentOutput {
  const {
    template = '',
    description = '',
    response = '',
    introduction = '',
    indication = '',
    choices,
  } = input;

  const variables = parseTemplate(template);

  const unesc = (s: string) => (s ? unescapeHtml(s) : '');
  let desc = substituteVariables(unesc(description), variables);
  let resp = substituteVariables(unesc(response), variables);
  let intro = substituteVariables(unesc(introduction), variables);
  let indic = substituteVariables(unesc(indication), variables);

  const compiledChoices = choices?.map(choice => {
    if (typeof choice !== 'string') return choice;
    const base = substituteVariables(unesc(choice), variables);
    return processEvalAndObject(base, variables);
  });

  desc = processEvalAndObject(desc, variables);
  resp = processEvalAndObject(resp, variables);
  intro = processEvalAndObject(intro, variables);
  indic = processEvalAndObject(indic, variables);

  return {
    description: desc,
    response: resp,
    introduction: intro,
    indication: indic,
    choices: compiledChoices,
  };
}

/**
 * Compile a single choice string without using template variables (only unescape + <eval>/<object>).
 * Useful when we just want to \"evaluate\" choices for display without impacting stored values.
 */
export function compileSlamChoiceText(text: string): string {
  if (!text) return text;
  const unescaped = unescapeHtml(text);
  // No variables here, just evaluate <eval> and <object> tags with an empty variable set.
  return processEvalAndObject(unescaped, []);
}
